import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import firebase from 'src/utils/firebase';
import { socketClient } from '../index';

const normalizeInvoicePayload = (payload) => {
  if (payload && Object.prototype.hasOwnProperty.call(payload, 'invoice')) {
    return payload.invoice;
  }
  return payload || null;
};
const requireInvoicePayload = (payload) => {
  const invoice = normalizeInvoicePayload(payload);
  if (!invoice?.id) {
    throw new Error('Work order not found');
  }
  return invoice;
};
const normalizeRequestedInvoiceId = (value) => {
  const invoiceId = Number(value);
  return Number.isSafeInteger(invoiceId) && invoiceId > 0 ? invoiceId : null;
};
const normalizeServicesPayload = (payload) =>
  Array.isArray(payload) ? payload : payload?.services || [];
const normalizeWarrantiesPayload = (payload) =>
  Array.isArray(payload) ? payload : payload?.warranties || [];
const normalizeTechniciansPayload = (payload) =>
  Array.isArray(payload) ? payload : payload?.technicians || [];
const normalizeInvoiceId = (value) => {
  const invoiceId = Number(value || 0);
  return Number.isSafeInteger(invoiceId) && invoiceId > 0 ? invoiceId : null;
};
const getWorksheetMutationInvoiceId = (action, fallbackInvoiceId = null) => {
  const arg = action.meta?.arg;
  const isOdometerMutation = action.type?.startsWith('odometerChanged/')
    || action.type?.startsWith('odometerChangedAtCompletion/');
  const argInvoiceId = typeof arg === 'object' && arg !== null
    ? (isOdometerMutation ? arg.id : arg.invoiceId ?? arg.invoice_id)
    : (isOdometerMutation ? arg : null);
  return normalizeInvoiceId(argInvoiceId) || normalizeInvoiceId(fallbackInvoiceId);
};
const ownsWorksheetMutation = (state, action) => {
  if (!state.worksheetMutationRequestId
    || state.worksheetMutationRequestId !== action.meta?.requestId) {
    return false;
  }

  const ownerInvoiceId = normalizeInvoiceId(state.worksheetMutationInvoiceId);
  const actionInvoiceId = getWorksheetMutationInvoiceId(action);
  const currentInvoiceId = normalizeInvoiceId(state.currentInvoice?.id);
  return (!ownerInvoiceId || !actionInvoiceId || ownerInvoiceId === actionInvoiceId)
    && (!ownerInvoiceId || !currentInvoiceId || ownerInvoiceId === currentInvoiceId);
};
const emitScopedInvoiceRequest = ({
  emitEvent,
  successEvent,
  errorEvent,
  payload,
  timeoutMessage,
  mapSuccess
}) => new Promise((resolve, reject) => {
  const handleSuccess = (response) => {
    if (response?.requestId !== payload.requestId) {
      return;
    }

    clearTimeout(timeoutId);
    socketClient.off(successEvent, handleSuccess);
    socketClient.off(errorEvent, handleError);
    try {
      resolve(mapSuccess ? mapSuccess(response) : response);
    } catch (error) {
      reject(error);
    }
  };

  const handleError = (response) => {
    if (response?.requestId !== payload.requestId) {
      return;
    }

    clearTimeout(timeoutId);
    socketClient.off(successEvent, handleSuccess);
    socketClient.off(errorEvent, handleError);
    reject(new Error(response?.message || timeoutMessage));
  };

  const timeoutId = setTimeout(() => {
    socketClient.off(successEvent, handleSuccess);
    socketClient.off(errorEvent, handleError);
    reject(new Error(timeoutMessage));
  }, 10000);

  socketClient.on(successEvent, handleSuccess);
  socketClient.on(errorEvent, handleError);
  socketClient.emit(emitEvent, payload);
});

const emitInvoiceMutation = async (eventName, payload = {}) => {
  socketClient.emit(eventName, {
    ...payload,
    idToken: await firebase.auth().currentUser.getIdToken()
  });
};

let activeCurrentInvoiceListener = null;
let activeCurrentInvoiceId = null;
const invoiceRealtimeListeners = {
  invoiceItemsReordered: null,
  similiarItems: null
};

const initialState = {
  loading: true,
  invoiceError: null,
  worksheetMutationLoading: false,
  worksheetMutationError: null,
  worksheetMutationRequestId: null,
  worksheetMutationInvoiceId: null,
  servicesLoading: false,
  servicesError: null,
  warrantiesLoading: false,
  warrantiesError: null,
  techniciansLoading: false,
  techniciansError: null,
  currentInvoice: {},
  currentInvoiceRequestId: null,
  currentInvoiceRequestedId: null,
  services: {},
  warranties: {},
  technicians: [],
  similarInvoices: [],
  similarItemId: null
};

export const changeTechnician = createAsyncThunk('changeTechnician', async function (payload) {
  socketClient.emit('changeTechnician', {
    invoiceId: payload.invoiceId,
    technician: payload.technician,
    idToken: await firebase.auth().currentUser.getIdToken()
  });
});

export const vinChanged = createAsyncThunk('vinChanged', async function (payload) {
  socketClient.emit('vinChanged', {
    vin: payload.vin,
    id: payload.id,
    idToken: await firebase.auth().currentUser.getIdToken()
  });
});

export const plateChanged = createAsyncThunk('plateChanged', async function (payload) {
  socketClient.emit('plateChanged', {
    plate: payload.plate,
    id: payload.id,
    idToken: await firebase.auth().currentUser.getIdToken()
  });
});

export const odometerChanged = createAsyncThunk('odometerChanged', async function (payload) {
  const requestId = `invoice-odometer:in:${payload.id}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'odometerChanged',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      odometer: payload.odometer,
      id: payload.id,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while updating mileage in',
    mapSuccess: normalizeInvoicePayload
  });
});

export const odometerChangedAtCompletion = createAsyncThunk(
  'odometerChangedAtCompletion',
  async function (payload) {
    const requestId = `invoice-odometer:out:${payload.id}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    return emitScopedInvoiceRequest({
      emitEvent: 'odometerAtCompletionChanged',
      successEvent: 'getInvoice',
      errorEvent: 'getInvoiceError',
      payload: {
        odometer: payload.odometer,
        id: payload.id,
        idToken: await firebase.auth().currentUser.getIdToken(),
        requestId
      },
      timeoutMessage: 'Timed out while updating mileage out',
      mapSuccess: normalizeInvoicePayload
    });
  }
);

export const notesChanged = createAsyncThunk('notesChanged', async function (payload) {
  socketClient.emit('notesChanged', {
    notes: payload.notes,
    id: payload.id,
    idToken: await firebase.auth().currentUser.getIdToken()
  });
});

export const partNumberChanged = createAsyncThunk('partNumberChanged', async function (payload) {
  socketClient.emit('partNumberChanged', {
    partNumber: payload.partNumber,
    id: payload.id,
    idToken: await firebase.auth().currentUser.getIdToken()
  });
});

export const getCurrentInvoice = createAsyncThunk(
  'invoices/getCurrentInvoice',
  async function (invoiceId, { rejectWithValue }) {
    try {
      if (!normalizeRequestedInvoiceId(invoiceId)) {
        throw new Error('Work order not found');
      }
      const requestId = `invoice:get:${invoiceId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
      const idToken = await firebase.auth().currentUser.getIdToken();

      return await emitScopedInvoiceRequest({
        emitEvent: 'getInvoice',
        successEvent: 'getInvoice',
        errorEvent: 'getInvoiceError',
        payload: { idToken, invoiceId, requestId },
        timeoutMessage: 'Timed out while loading work order',
        mapSuccess: requireInvoicePayload
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to load work order');
    }
  }
);

export const subscribeCurrentInvoice = createAsyncThunk(
  'invoices/subscribeCurrentInvoice',
  async function (invoiceId, { dispatch }) {
    if (activeCurrentInvoiceListener) {
      socketClient.off('getInvoice', activeCurrentInvoiceListener);
      activeCurrentInvoiceListener = null;
    }

    activeCurrentInvoiceId = Number(invoiceId);
    activeCurrentInvoiceListener = (response) => {
      // Correlated replies belong to the scoped request that owns requestId.
      // This listener consumes only uncorrelated realtime publications.
      if (response && Object.prototype.hasOwnProperty.call(response, 'requestId')) {
        return;
      }
      const invoice = normalizeInvoicePayload(response);

      if (!invoice?.id || Number(invoice.id) !== activeCurrentInvoiceId) {
        return;
      }

      dispatch(slice.actions.fetchCurrentInvoice(invoice));
    };

    socketClient.on('getInvoice', activeCurrentInvoiceListener);
  }
);

export const unsubscribeCurrentInvoice = createAsyncThunk(
  'invoices/unsubscribeCurrentInvoice',
  async function () {
    if (activeCurrentInvoiceListener) {
      socketClient.off('getInvoice', activeCurrentInvoiceListener);
      activeCurrentInvoiceListener = null;
    }

    activeCurrentInvoiceId = null;
  }
);

export const subscribeInvoiceRealtime = createAsyncThunk(
  'invoices/subscribeInvoiceRealtime',
  async function (invoiceId, { dispatch, getState }) {
    if (invoiceRealtimeListeners.invoiceItemsReordered) {
      socketClient.off('invoiceItemsReordered', invoiceRealtimeListeners.invoiceItemsReordered);
    }
    if (invoiceRealtimeListeners.similiarItems) {
      socketClient.off('similiarItems', invoiceRealtimeListeners.similiarItems);
    }

    const numericInvoiceId = Number(invoiceId);

    invoiceRealtimeListeners.invoiceItemsReordered = (response) => {
      const items = Array.isArray(response) ? response : [];
      if (!items.length || Number(items[0]?.invoice_id) !== numericInvoiceId) {
        return;
      }

      dispatch(slice.actions.invoiceItemsReordered(items));
    };

    invoiceRealtimeListeners.similiarItems = (response) => {
      const currentItems = getState().invoices.currentInvoice?.invoiceItems || [];
      const itemIds = currentItems.map((item) => Number(item.id));
      if (!itemIds.includes(Number(response?.item_id))) {
        return;
      }

      dispatch(slice.actions.fetchSimilarInvoices(response));
    };

    socketClient.on('invoiceItemsReordered', invoiceRealtimeListeners.invoiceItemsReordered);
    socketClient.on('similiarItems', invoiceRealtimeListeners.similiarItems);
  }
);

export const unsubscribeInvoiceRealtime = createAsyncThunk(
  'invoices/unsubscribeInvoiceRealtime',
  async function () {
    if (invoiceRealtimeListeners.invoiceItemsReordered) {
      socketClient.off('invoiceItemsReordered', invoiceRealtimeListeners.invoiceItemsReordered);
      invoiceRealtimeListeners.invoiceItemsReordered = null;
    }
    if (invoiceRealtimeListeners.similiarItems) {
      socketClient.off('similiarItems', invoiceRealtimeListeners.similiarItems);
      invoiceRealtimeListeners.similiarItems = null;
    }
  }
);

export const createInvoiceItem = createAsyncThunk('createInvoiceItem', async function (invoiceId) {
  await emitInvoiceMutation('createInvoiceItem', {
    id: invoiceId
  });
});

export const createInvoiceConcern = createAsyncThunk('createInvoiceConcern', async function (payload) {
  const requestId = `invoice-worksheet:create-concern:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'createInvoiceConcern',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while creating concern',
    mapSuccess: normalizeInvoicePayload
  });
});

export const updateInvoiceConcern = createAsyncThunk('updateInvoiceConcern', async function (payload) {
  const requestId = `invoice-worksheet:update-concern:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'updateInvoiceConcern',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while updating concern',
    mapSuccess: normalizeInvoicePayload
  });
});

export const deleteInvoiceConcernEntry = createAsyncThunk('deleteInvoiceConcernEntry', async function (concernId) {
  const requestId = `invoice-worksheet:delete-concern:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'deleteInvoiceConcernEntry',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      concernId,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while deleting concern',
    mapSuccess: normalizeInvoicePayload
  });
});

export const copyInvoiceConcernToJob = createAsyncThunk('copyInvoiceConcernToJob', async function (concernId) {
  const requestId = `invoice-worksheet:copy-concern:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'copyInvoiceConcernToJob',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      concernId,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while copying concern to job',
    mapSuccess: normalizeInvoicePayload
  });
});

export const copyDeclinedHistoryJobToInvoice = createAsyncThunk(
  'copyDeclinedHistoryJobToInvoice',
  async function (payload) {
    const requestId = `invoice-worksheet:copy-declined-history:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    return emitScopedInvoiceRequest({
      emitEvent: 'copyDeclinedHistoryJobToInvoice',
      successEvent: 'getInvoice',
      errorEvent: 'getInvoiceError',
      payload: {
        ...payload,
        idToken: await firebase.auth().currentUser.getIdToken(),
        requestId
      },
      timeoutMessage: 'Timed out while copying declined job history',
      mapSuccess: normalizeInvoicePayload
    });
  }
);

export const createInvoiceJob = createAsyncThunk('createInvoiceJob', async function (payload) {
  const requestId = `invoice-worksheet:create-job:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'createInvoiceJob',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while creating job',
    mapSuccess: normalizeInvoicePayload
  });
});

export const updateInvoiceJob = createAsyncThunk('updateInvoiceJob', async function (payload) {
  const requestId = `invoice-worksheet:update-job:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'updateInvoiceJob',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while updating job',
    mapSuccess: normalizeInvoicePayload
  });
});

export const deleteInvoiceJob = createAsyncThunk('deleteInvoiceJob', async function (jobId) {
  const requestId = `invoice-worksheet:delete-job:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'deleteInvoiceJob',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      jobId,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while deleting job',
    mapSuccess: normalizeInvoicePayload
  });
});

export const changeInvoiceJobApproval = createAsyncThunk('changeInvoiceJobApproval', async function (payload) {
  const requestId = `invoice-worksheet:job-approval:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'changeInvoiceJobApproval',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while updating approval',
    mapSuccess: normalizeInvoicePayload
  });
});

export const authorizeInvoiceJobs = createAsyncThunk('authorizeInvoiceJobs', async function (payload) {
  const requestId = `invoice-worksheet:authorize:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  const normalizedPayload =
    typeof payload === 'number'
      ? { invoiceId: payload }
      : payload || {};
  return emitScopedInvoiceRequest({
    emitEvent: 'authorizeInvoiceJobs',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...normalizedPayload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while authorizing work',
    mapSuccess: normalizeInvoicePayload
  });
});

export const reviewInvoiceAuthorizationDecisions = createAsyncThunk(
  'reviewInvoiceAuthorizationDecisions',
  async function (payload) {
    const requestId = `invoice-worksheet:review-authorization:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    return emitScopedInvoiceRequest({
      emitEvent: 'reviewInvoiceAuthorizationDecisions',
      successEvent: 'getInvoice',
      errorEvent: 'getInvoiceError',
      payload: {
        ...payload,
        idToken: await firebase.auth().currentUser.getIdToken(),
        requestId
      },
      timeoutMessage: 'Timed out while reviewing customer decisions',
      mapSuccess: normalizeInvoicePayload
    });
  }
);

export const changeInvoiceJobWorkflowStatus = createAsyncThunk('changeInvoiceJobWorkflowStatus', async function (payload) {
  const requestId = `invoice-worksheet:workflow:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'changeInvoiceJobWorkflowStatus',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while updating workflow status',
    mapSuccess: normalizeInvoicePayload
  });
});

export const assignInvoiceJobTechnician = createAsyncThunk('assignInvoiceJobTechnician', async function (payload) {
  const requestId = `invoice-worksheet:assign-tech:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'assignInvoiceJobTechnician',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while assigning technician',
    mapSuccess: normalizeInvoicePayload
  });
});

export const reorderInvoiceJobs = createAsyncThunk('reorderInvoiceJobs', async function (payload) {
  const requestId = `invoice-worksheet:reorder-jobs:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'reorderInvoiceJobs',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while reordering jobs',
    mapSuccess: normalizeInvoicePayload
  });
});

export const createInvoiceJobLine = createAsyncThunk('createInvoiceJobLine', async function (payload) {
  const requestId = `invoice-worksheet:create-line:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'createInvoiceJobLine',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while creating line',
    mapSuccess: normalizeInvoicePayload
  });
});

export const updateInvoiceJobLine = createAsyncThunk('updateInvoiceJobLine', async function (payload) {
  const requestId = `invoice-worksheet:update-line:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'updateInvoiceJobLine',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while updating line',
    mapSuccess: normalizeInvoicePayload
  });
});

export const deleteInvoiceJobLine = createAsyncThunk('deleteInvoiceJobLine', async function (lineId) {
  const requestId = `invoice-worksheet:delete-line:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'deleteInvoiceJobLine',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      lineId,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while deleting line',
    mapSuccess: normalizeInvoicePayload
  });
});

export const reorderInvoiceJobLines = createAsyncThunk('reorderInvoiceJobLines', async function (payload) {
  const requestId = `invoice-worksheet:reorder-lines:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'reorderInvoiceJobLines',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while reordering job lines',
    mapSuccess: normalizeInvoicePayload
  });
});

export const createInvoiceFee = createAsyncThunk('createInvoiceFee', async function (payload) {
  const requestId = `invoice-worksheet:create-fee:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'createInvoiceFee',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while creating fee',
    mapSuccess: normalizeInvoicePayload
  });
});

export const updateInvoiceFee = createAsyncThunk('updateInvoiceFee', async function (payload) {
  const requestId = `invoice-worksheet:update-fee:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'updateInvoiceFee',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      ...payload,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while updating fee',
    mapSuccess: normalizeInvoicePayload
  });
});

export const deleteInvoiceFee = createAsyncThunk('deleteInvoiceFee', async function (feeId) {
  const requestId = `invoice-worksheet:delete-fee:${Date.now()}:${Math.random().toString(36).slice(2)}`;
  return emitScopedInvoiceRequest({
    emitEvent: 'deleteInvoiceFee',
    successEvent: 'getInvoice',
    errorEvent: 'getInvoiceError',
    payload: {
      feeId,
      idToken: await firebase.auth().currentUser.getIdToken(),
      requestId
    },
    timeoutMessage: 'Timed out while deleting fee',
    mapSuccess: normalizeInvoicePayload
  });
});

export const deleteInvoiceItem = createAsyncThunk('deleteInvoiceItem', async function (invoiceId) {
  await emitInvoiceMutation('deleteInvoiceItem', {
    id: invoiceId
  });
});

export const descriptionChanged = createAsyncThunk('descriptionChanged', async function (payload) {
  await emitInvoiceMutation('descriptionChanged', {
    description: payload.description,
    id: payload.id
  });
});

export const getServices = createAsyncThunk(
  'invoices/getServices',
  async function (_, { rejectWithValue }) {
    try {
      const requestId = `invoice-services:list:${Date.now()}:${Math.random().toString(36).slice(2)}`;
      const idToken = await firebase.auth().currentUser.getIdToken();

      return await emitScopedInvoiceRequest({
        emitEvent: 'getServices',
        successEvent: 'getServices',
        errorEvent: 'getServicesError',
        payload: { idToken, requestId },
        timeoutMessage: 'Timed out while loading services',
        mapSuccess: normalizeServicesPayload
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to load services');
    }
  }
);

export const changePricePerUnit = createAsyncThunk('changePricePerUnit', async function (payload) {
  socketClient.emit('pricePerUnitChanged', {
    pricePerUnit: payload.pricePerUnit,
    id: payload.id,
    idToken: await firebase.auth().currentUser.getIdToken()
  });
});

export const changeQuantity = createAsyncThunk('changeQuantity', async function (payload) {
  socketClient.emit('quantityChanged', {
    quantity: payload.quantity,
    id: payload.id,
    idToken: await firebase.auth().currentUser.getIdToken()
  });
});

export const getWarranties = createAsyncThunk(
  'invoices/getWarranties',
  async function (_, { rejectWithValue }) {
    try {
      const requestId = `invoice-warranties:list:${Date.now()}:${Math.random().toString(36).slice(2)}`;
      const idToken = await firebase.auth().currentUser.getIdToken();

      return await emitScopedInvoiceRequest({
        emitEvent: 'getWarranties',
        successEvent: 'warrantiesFetched',
        errorEvent: 'getWarrantiesError',
        payload: { idToken, requestId },
        timeoutMessage: 'Timed out while loading warranties',
        mapSuccess: normalizeWarrantiesPayload
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to load warranties');
    }
  }
);

export const changeServiceType = createAsyncThunk('changeServiceType', async function (payload) {
  socketClient.emit('serviceChanged', {
    idToken: await firebase.auth().currentUser.getIdToken(),
    id: payload.id,
    service: payload.serviceType
  });
});

export const updateFees = createAsyncThunk('updateFees', async function (payload) {
  socketClient.emit('updateFees', {
    idToken: await firebase.auth().currentUser.getIdToken(),
    id: payload.id,
    fees: payload.fees
  });
});

export const changeCostPerUnit = createAsyncThunk('changeCostPerUnit', async function (payload) {
  socketClient.emit('costChanged', {
    idToken: await firebase.auth().currentUser.getIdToken(),
    id: payload.id,
    cost: payload.costPerUnit
  });
});

export const requestGetAllTechnicians = createAsyncThunk(
  'invoices/requestGetAllTechnicians',
  async function (_, { rejectWithValue }) {
    try {
      const requestId = `invoice-technicians:list:${Date.now()}:${Math.random().toString(36).slice(2)}`;
      const idToken = await firebase.auth().currentUser.getIdToken();

      return await emitScopedInvoiceRequest({
        emitEvent: 'getTechnicians',
        successEvent: 'getTechnicians',
        errorEvent: 'getTechniciansError',
        payload: { idToken, requestId },
        timeoutMessage: 'Timed out while loading technicians',
        mapSuccess: normalizeTechniciansPayload
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to load technicians');
    }
  }
);

export const reportToCarfaxChanged = createAsyncThunk('reportToCarfaxChanged', async function (payload) {
  socketClient.emit('reportToCarfaxChanged', {
    id: payload.id,
    report_to_carfax: payload.report_to_carfax,
    idToken: await firebase.auth().currentUser.getIdToken()
  });
});

export const smsReviewChanged = createAsyncThunk('smsReviewChanged', async function (payload) {
  socketClient.emit('smsReviewChanged', {
    id: payload.id,
    sms_review: payload.sms_review,
    idToken: await firebase.auth().currentUser.getIdToken()
  });
});

export const sendInvoiceViaSms = createAsyncThunk(
  'sendInvoiceViaSms',
  async function (payload, { rejectWithValue }) {
    try {
      const requestId = `invoice:send:${payload.id}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
      return await emitScopedInvoiceRequest({
        emitEvent: 'sendInvoice',
        successEvent: 'sendInvoiceSuccess',
        errorEvent: 'sendInvoiceError',
        payload: {
          id: payload.id,
          idToken: await firebase.auth().currentUser.getIdToken(),
          requestId
        },
        timeoutMessage: 'Timed out while sending the customer document.'
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to send the customer document.');
    }
  }
);

export const reorderInvoiceItems = createAsyncThunk('reorderInvoiceItems', async function (payload) {
  socketClient.emit('reorderInvoiceItems', {
    idToken: await firebase.auth().currentUser.getIdToken(),
    invoiceId: payload.invoiceId,
    items: payload.items
  });
});

const worksheetMutationThunks = [
  odometerChanged,
  odometerChangedAtCompletion,
  createInvoiceConcern,
  updateInvoiceConcern,
  deleteInvoiceConcernEntry,
  copyInvoiceConcernToJob,
  copyDeclinedHistoryJobToInvoice,
  createInvoiceJob,
  updateInvoiceJob,
  deleteInvoiceJob,
  changeInvoiceJobApproval,
  authorizeInvoiceJobs,
  reviewInvoiceAuthorizationDecisions,
  changeInvoiceJobWorkflowStatus,
  assignInvoiceJobTechnician,
  reorderInvoiceJobs,
  createInvoiceJobLine,
  deleteInvoiceJobLine,
  reorderInvoiceJobLines,
  createInvoiceFee,
  updateInvoiceFee,
  deleteInvoiceFee
];

const worksheetMutationPendingTypes = worksheetMutationThunks.map((thunk) => thunk.pending.type);
const worksheetMutationFulfilledTypes = worksheetMutationThunks.map((thunk) => thunk.fulfilled.type);
const worksheetMutationRejectedTypes = worksheetMutationThunks.map((thunk) => thunk.rejected.type);

const slice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    fetchSimilarInvoices(state, action) {
      state.similarInvoices = action.payload.invoicesFound;
      state.similarItemId = action.payload.item_id;
    },
    invoiceItemsReordered(state, action) {
      state.currentInvoice.invoiceItems = action.payload;
    },
    getInvoice(state) {
      state.loading = true;
    },
    getTechnicians(state, action) {
      state.technicians = action.payload;
    },
    fetchCurrentInvoice(state, action) {
      const previousInvoiceId = normalizeInvoiceId(state.currentInvoice?.id);
      const nextInvoiceId = normalizeInvoiceId(action.payload?.id);
      if (previousInvoiceId && nextInvoiceId && previousInvoiceId !== nextInvoiceId) {
        state.worksheetMutationLoading = false;
        state.worksheetMutationError = null;
        state.worksheetMutationRequestId = null;
        state.worksheetMutationInvoiceId = null;
      }
      state.currentInvoiceRequestId = null;
      state.currentInvoiceRequestedId = null;
      state.currentInvoice = action.payload;
      state.loading = false;
      state.invoiceError = null;
      state.similarInvoices = [];
    },
    fetchGetServices(state, action) {
      state.services = action.payload;
    },
    fetchWarranties(state, action) {
      state.warranties = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getCurrentInvoice.pending, (state, action) => {
      const requestedInvoiceId = normalizeRequestedInvoiceId(action.meta.arg);
      const currentInvoiceId = Number(state.currentInvoice?.id || 0);
      if (currentInvoiceId && currentInvoiceId !== requestedInvoiceId) {
        state.worksheetMutationLoading = false;
        state.worksheetMutationError = null;
        state.worksheetMutationRequestId = null;
        state.worksheetMutationInvoiceId = null;
      }
      state.currentInvoiceRequestId = action.meta.requestId;
      state.currentInvoiceRequestedId = requestedInvoiceId;
      state.loading = !currentInvoiceId || currentInvoiceId !== requestedInvoiceId;
      if (currentInvoiceId !== requestedInvoiceId) {
        state.currentInvoice = {};
        state.similarInvoices = [];
      }
      state.invoiceError = null;
    });
    builder.addCase(getCurrentInvoice.fulfilled, (state, action) => {
      const previousInvoiceId = normalizeInvoiceId(state.currentInvoice?.id);
      const nextInvoiceId = normalizeInvoiceId(action.payload?.id);
      if (previousInvoiceId && nextInvoiceId && previousInvoiceId !== nextInvoiceId) {
        state.worksheetMutationLoading = false;
        state.worksheetMutationError = null;
        state.worksheetMutationRequestId = null;
        state.worksheetMutationInvoiceId = null;
      }
      const requestedInvoiceId = normalizeRequestedInvoiceId(action.meta.arg);
      if (
        state.currentInvoiceRequestId !== action.meta.requestId ||
        state.currentInvoiceRequestedId !== requestedInvoiceId
      ) {
        return;
      }
      state.currentInvoiceRequestId = null;
      state.currentInvoiceRequestedId = null;
      state.currentInvoice = action.payload || {};
      state.loading = false;
      state.invoiceError = null;
      state.similarInvoices = [];
    });
    builder.addCase(getCurrentInvoice.rejected, (state, action) => {
      const requestedInvoiceId = normalizeRequestedInvoiceId(action.meta.arg);
      if (
        state.currentInvoiceRequestId !== action.meta.requestId ||
        state.currentInvoiceRequestedId !== requestedInvoiceId
      ) {
        return;
      }
      state.currentInvoiceRequestId = null;
      state.currentInvoiceRequestedId = null;
      state.currentInvoice = {};
      state.loading = false;
      state.invoiceError = action.payload || action.error?.message || 'Unable to load work order';
      state.similarInvoices = [];
    });
    builder.addCase(getServices.pending, (state) => {
      state.servicesLoading = true;
      state.servicesError = null;
    });
    builder.addCase(getServices.fulfilled, (state, action) => {
      state.servicesLoading = false;
      state.services = action.payload || [];
      state.servicesError = null;
    });
    builder.addCase(getServices.rejected, (state, action) => {
      state.servicesLoading = false;
      state.servicesError = action.payload || action.error?.message || 'Unable to load services';
    });
    builder.addCase(getWarranties.pending, (state) => {
      state.warrantiesLoading = true;
      state.warrantiesError = null;
    });
    builder.addCase(getWarranties.fulfilled, (state, action) => {
      state.warrantiesLoading = false;
      state.warranties = action.payload || [];
      state.warrantiesError = null;
    });
    builder.addCase(getWarranties.rejected, (state, action) => {
      state.warrantiesLoading = false;
      state.warrantiesError = action.payload || action.error?.message || 'Unable to load warranties';
    });
    builder.addCase(requestGetAllTechnicians.pending, (state) => {
      state.techniciansLoading = true;
      state.techniciansError = null;
    });
    builder.addCase(requestGetAllTechnicians.fulfilled, (state, action) => {
      state.techniciansLoading = false;
      state.technicians = action.payload || [];
      state.techniciansError = null;
    });
    builder.addCase(requestGetAllTechnicians.rejected, (state, action) => {
      state.techniciansLoading = false;
      state.techniciansError = action.payload || action.error?.message || 'Unable to load technicians';
    });
    builder.addCase(updateInvoiceJobLine.fulfilled, (state, action) => {
      if (action.payload) {
        state.currentInvoice = action.payload;
        state.similarInvoices = [];
      }
    });
    builder.addCase(updateInvoiceJobLine.rejected, (state, action) => {
      state.worksheetMutationError =
        action.payload || action.error?.message || 'Unable to update worksheet line';
    });
    builder.addMatcher(
      (action) => worksheetMutationPendingTypes.includes(action.type),
      (state, action) => {
        state.worksheetMutationLoading = true;
        state.worksheetMutationError = null;
        state.worksheetMutationRequestId = action.meta?.requestId || null;
        state.worksheetMutationInvoiceId = getWorksheetMutationInvoiceId(
          action,
          state.currentInvoice?.id
        );
      }
    );
    builder.addMatcher(
      (action) => worksheetMutationFulfilledTypes.includes(action.type),
      (state, action) => {
        if (!ownsWorksheetMutation(state, action)) return;
        state.worksheetMutationLoading = false;
        state.worksheetMutationError = null;
        state.worksheetMutationRequestId = null;
        state.worksheetMutationInvoiceId = null;
        const responseInvoiceId = Number(action.payload?.id || 0);
        const currentInvoiceId = Number(state.currentInvoice?.id || 0);
        if (action.payload && (!currentInvoiceId || !responseInvoiceId || currentInvoiceId === responseInvoiceId)) {
          state.currentInvoice = action.payload;
          state.similarInvoices = [];
        }
      }
    );
    builder.addMatcher(
      (action) => worksheetMutationRejectedTypes.includes(action.type),
      (state, action) => {
        if (!ownsWorksheetMutation(state, action)) return;
        state.worksheetMutationLoading = false;
        state.worksheetMutationError =
          action.payload || action.error?.message || 'Unable to update worksheet';
        state.worksheetMutationRequestId = null;
        state.worksheetMutationInvoiceId = null;
      }
    );
  }
});

export const reducer = slice.reducer;
export default slice;
